/**
 * ENG-6338 (epic ENG-6333 — graceful degradation): safe, health-gated
 * auto-resume for circuit-breaker pauses — the recovery loop.
 *
 * ENG-6088 ({@link ./auto-resume.ts}) already grants a tripped agent ONE
 * bounded auto-resume, but on a BLIND quiet timer: 30 min with no further
 * restart event and it resumes, hoping the storm passed. That's a probe, not a
 * recovery — it never checks whether the dependency that drove the restart
 * loop actually came back. Done naively, auto-resume re-creates the exact
 * crash-loop the breaker exists to stop. This reconciler is the careful
 * version.
 *
 * It replaces the timer with a GENUINE-HEALTH gate. A tripped agent is cleared
 * only when its dependency has demonstrably recovered:
 *
 *   - authoritative status = active (the recovered DEPENDENCY — the paused
 *     agent's own status is, by definition, `paused`),
 *   - its MCP server is present in the agent's desired state, and
 *   - connectivity has read `ok` for >= `minHealthyCycles` consecutive cycles
 *     (hysteresis — the reverse of `consecutive_connectivity_failures`, so a
 *     single flap-to-ok can't resume into a still-sick dependency).
 *
 * Binding invariants (council-resolved, do not relax):
 *   - The breaker stays AUTHORITATIVE. Clearing a trip NEVER resets the
 *     restart counter — this decision only READS the trip + the credit marker
 *     and cannot reset anything. The caller's `restartBreaker.clear()` drops
 *     the trip; the spent-credit marker persists across the window.
 *   - ONE auto-resume per dependency per window. The credit marker (shared
 *     with ENG-6088, persisted in `manager-state.json`) is the cap.
 *   - A resumed agent that RE-TRIPS within the window is UNSTABLE, not down.
 *     It latches to mandatory-manual and the manager is told the dependency is
 *     unstable — a distinct signal from the original "down" alert. No
 *     oscillation: at most one auto-resume, then a human decides.
 *
 * Pure state machine, no I/O: the health snapshot is INJECTED (gathered
 * server-side, where the connectivity/MCP truth lives, and handed to the
 * manager over its existing poll — event for latency, poll for correctness).
 * The caller owns the API call, the marker map, and the trip clear. Tests
 * drive `now` + the snapshot directly.
 *
 * Rollout: gated OFF by default via the `resume-reconciler` registry flag
 * (ENG-6395; env override `AGT_RESUME_RECONCILER_ENABLED` → heartbeat →
 * compiled default), layered over ENG-6088 — when off, the blind-timer
 * {@link decideAutoResume} still governs; when on, this health gate does. The
 * manager reads the flag via its host flag store; `readResumeReconcilerConfig`
 * here still supplies the numeric knobs (minHealthyCycles, backoffResetMs) and
 * the env-only `enabled` fallback the pure decision uses. Flip per-host
 * centrally after a canary bake.
 */

import type { TripState } from './restart-breaker.js';
import type { AutoResumeRecord } from './auto-resume.js';
import { readEnvNumber } from './restart-breaker.js';

/**
 * Genuine-health snapshot for the trip-driving dependency, gathered
 * server-side and injected into the decision. Every field is a recovery
 * signal; all three must hold before a resume.
 */
export interface ResumeHealthSnapshot {
  /**
   * Authoritative status of the recovered dependency is `active` — NOT the
   * paused agent's own status (which is `paused` for the duration of the trip).
   */
  dependencyStatusActive: boolean;
  /** The dependency's MCP server is present in the agent's desired (.mcp.json) state. */
  mcpPresent: boolean;
  /**
   * Consecutive cycles connectivity has read `ok` (reverse hysteresis; resets
   * to 0 on any non-`ok` reading, the mirror of `consecutive_connectivity_failures`).
   */
  connectivityOkCycles: number;
}

export interface ResumeReconcilerConfig {
  enabled: boolean;
  /** Connectivity must read `ok` for this many consecutive cycles before a resume. */
  minHealthyCycles: number;
  /** A spent credit re-arms after this long (shared with ENG-6088's marker TTL). */
  backoffResetMs: number;
}

const DEFAULT_MIN_HEALTHY_CYCLES = 2;
const DEFAULT_BACKOFF_RESET_MS = 86_400_000; // 24 h — matches AGT_AUTO_RESUME_BACKOFF_RESET_MS default.

export function readResumeReconcilerConfig(
  env: NodeJS.ProcessEnv = process.env,
): ResumeReconcilerConfig {
  const raw = (env['AGT_RESUME_RECONCILER_ENABLED'] ?? '').trim().toLowerCase();
  const minCycles = readEnvNumber('AGT_RESUME_MIN_HEALTHY_CYCLES', DEFAULT_MIN_HEALTHY_CYCLES);
  return {
    enabled: raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on',
    // A fractional / <1 override is meaningless for a cycle count — floor to an
    // integer and demand at least 1, so "healthy" always means "observed at
    // least once". readEnvNumber already rejects NaN / <=0 to the default.
    minHealthyCycles: Math.max(1, Math.floor(minCycles)),
    backoffResetMs: readEnvNumber('AGT_AUTO_RESUME_BACKOFF_RESET_MS', DEFAULT_BACKOFF_RESET_MS),
  };
}

/** Which recovery precondition is still failing — low-cardinality, for logs. */
export type ResumePrecondition = 'status' | 'mcp' | 'connectivity';

export type ResumeReconcileDecision =
  | { action: 'resume'; healthyCycles: number }
  /** A resumed agent re-tripped within the window: latch to manual + tell the manager it's unstable. */
  | { action: 'latch-unstable'; trippedAt: number }
  | {
      action: 'hold';
      reason: 'disabled' | 'no-trip' | 'not-healthy-yet';
      /** For 'not-healthy-yet': which precondition is still failing. */
      precondition?: ResumePrecondition;
      /** For a connectivity hold: cycles still needed to reach the hysteresis floor. */
      cyclesShort?: number;
    };

/**
 * Decide whether a tripped, paused agent should be cleared for auto-resume now.
 *
 * Order matters: the credit / unstable check precedes the health gate, because
 * a resumed agent that re-trips must latch to manual EVEN IF its dependency now
 * reads healthy again — that brief health is exactly the oscillation we refuse
 * to chase. `marker` is the persisted {@link AutoResumeRecord} for this agent
 * (shared with ENG-6088); a live marker means the one credit is spent.
 */
export function decideResumeReconcile(opts: {
  trip: TripState | undefined;
  marker: AutoResumeRecord | undefined;
  health: ResumeHealthSnapshot;
  config: ResumeReconcilerConfig;
  now: number;
}): ResumeReconcileDecision {
  const { trip, marker, health, config, now } = opts;
  if (!config.enabled) return { action: 'hold', reason: 'disabled' };
  if (!trip) return { action: 'hold', reason: 'no-trip' };

  // Credit gate. A LIVE marker means this dependency already spent its one
  // auto-resume this window. The marker is only written AFTER a successful
  // resume (which clears the trip), so a live marker ALONGSIDE a trip means the
  // agent we resumed has TRIPPED AGAIN — unstable, not down. Latch to manual;
  // never hand a second credit.
  if (marker && now - marker.autoResumedAt < config.backoffResetMs) {
    // Defensive: a trip carrying the SAME trippedAt the marker resumed is the
    // pre-clear trip still in flight (resume POST not yet observed), not a
    // re-trip — hold rather than mislabel an in-flight resume as unstable.
    if (trip.trippedAt === marker.trippedAt) {
      return { action: 'hold', reason: 'not-healthy-yet' };
    }
    return { action: 'latch-unstable', trippedAt: trip.trippedAt };
  }

  // Health gate (credit available — a fresh trip, or the TTL re-armed it). All
  // three preconditions must hold; report the FIRST that fails so the caller
  // logs a precise, low-cardinality reason.
  if (!health.dependencyStatusActive) {
    return { action: 'hold', reason: 'not-healthy-yet', precondition: 'status' };
  }
  if (!health.mcpPresent) {
    return { action: 'hold', reason: 'not-healthy-yet', precondition: 'mcp' };
  }
  if (health.connectivityOkCycles < config.minHealthyCycles) {
    return {
      action: 'hold',
      reason: 'not-healthy-yet',
      precondition: 'connectivity',
      cyclesShort: config.minHealthyCycles - Math.max(0, health.connectivityOkCycles),
    };
  }

  return { action: 'resume', healthyCycles: health.connectivityOkCycles };
}
